import { capitalCase } from 'case-anything';
import { type FC, useContext, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Form,
  Modal,
  ModalFooter,
  OverlayTrigger,
  Popover,
  Row,
} from 'react-bootstrap';
import type * as icons from 'react-bootstrap-icons';
import { TicketPerforated } from 'react-bootstrap-icons';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { Icon } from '../../Menu/Icon';
import './styles/styles.css';
import VerticalDotMenuIcon from '../../../assets/vertical-dot-menu.svg';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { useAuth, useRequest } from '../../../modules/hooks';
import type {
  CombinedRegistrationStep,
  MewsReservation,
  ReservationRegistrationStep,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { mergeRegistrationSteps } from '../../../modules/utils/registrationSteps';
import { GuestPortalManagerCard } from './GuestPortalManagerCard';

export interface ModalComponentData {
  show: boolean;
  reservation?: MewsReservation;
  handleClose: () => void;
}

interface StepItem {
  step: CombinedRegistrationStep;
}

interface SkippedStep {
  reservationRegistrationStepId: string;
  propertyRegistrationStepId: string;
  isSkippable: boolean;
}

const statusColorMapper: Record<string, string> = {
  COMPLETE: 'primary',
  INCOMPLETE: 'warning',
  DISABLED: 'secondary',
};

const statusMapper: Record<string, string> = {
  VERIFIED: 'secondary',
  UNVERIFIED: 'warning',
};

interface FormValues {
  skippedSteps: SkippedStep[];
}

const steps = [
  {
    name: 'User details',
    value: 'user-details',
    icon: 'ListTask',
    state: 'complete',
  },
  {
    name: 'Id upload',
    value: 'id-upload',
    icon: 'PersonVcard',
    state: 'incomplete',
  },
  {
    name: 'Agreement',
    value: 'agreement',
    icon: 'FileEarmarkText',
    state: 'incomplete',
  },
  {
    name: 'Safety deposit',
    value: 'safety_deposit',
    icon: 'CashCoin',
    state: 'incomplete',
  },
];

const renderMenuIcon = () => {
  return (
    <div className="menu-icon-container-style">
      <OverlayTrigger
        trigger="click"
        placement="left"
        rootClose
        overlay={
          <Popover id="popover-basic">
            <Popover.Body>
              <div
                onClick={() => {
                  // TODO: implement functionality
                }}
                style={{ paddingBottom: '8px' }}
                className="edit-damage-report-container"
              >
                <div className="edit-text-style">Edit</div>
              </div>
            </Popover.Body>
          </Popover>
        }
      >
        <div>
          <img src={VerticalDotMenuIcon} alt="vertical-icon" />
        </div>
      </OverlayTrigger>
    </div>
  );
};

export const ReservationStep = ({ step }: StepItem) => {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'skippedSteps',
  });

  const field = fields.find(
    (f) => f.reservationRegistrationStepId === step.reservationRegistrationStepId,
  );

  const isChecked = field?.isSkippable ?? step.isSkippable;

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    const existingIndex = fields.findIndex(
      (f) => f.reservationRegistrationStepId === step.reservationRegistrationStepId,
    );

    if (checked) {
      if (existingIndex === -1) {
        append({
          reservationRegistrationStepId: step.reservationRegistrationStepId,
          propertyRegistrationStepId: step.propertyRegistrationStepId,
          isSkippable: true,
        });
      } else {
        remove(existingIndex);
        append({
          reservationRegistrationStepId: step.reservationRegistrationStepId,
          propertyRegistrationStepId: step.propertyRegistrationStepId,
          isSkippable: true,
        });
      }
    } else if (existingIndex === -1) {
      append({
        reservationRegistrationStepId: step.reservationRegistrationStepId,
        propertyRegistrationStepId: step.propertyRegistrationStepId,
        isSkippable: false,
      });
    } else {
      remove(existingIndex);
      append({
        reservationRegistrationStepId: step.reservationRegistrationStepId,
        propertyRegistrationStepId: step.propertyRegistrationStepId,
        isSkippable: false,
      });
    }
  };

  return (
    <Row className="align-items-center mb-4">
      <Col xs={2}>
        <div
          className="rounded-circle bg-light d-flex justify-content-center align-items-center"
          style={{ width: 36, height: 36 }}
        >
          <Icon
            iconName={step?.icon as keyof typeof icons}
            size={18}
            className="align-top step-icon"
          />
        </div>
      </Col>
      <Col xs={4}>
        <div className="d-flex flex-column">
          <span>{step?.name}</span>
        </div>
      </Col>
      <Col xs={3}>
        <Badge
          className="status-badge"
          bg={statusColorMapper[step.isCompleted ? 'COMPLETE' : 'INCOMPLETE']}
        >
          {capitalCase(step.isCompleted ? 'complete' : 'incomplete')}
        </Badge>
      </Col>

      <Col xs={2}>
        <Form.Check
          type="checkbox"
          label="Skip"
          checked={isChecked}
          onChange={handleCheck}
        />
      </Col>
      <Col xs={1}>{renderMenuIcon()}</Col>
    </Row>
  );
};

export const GuestPortalManagerModal: FC<ModalComponentData> = ({
  show,
  handleClose,
  reservation,
}) => {
  const [currentState, setCurrentState] = useState('Unverified');
  const { credentialsInfo } = useAuth()!;
  const [combinedSteps, setCombinedSteps] = useState<CombinedRegistrationStep[]>([]);

  const { propertyRegistrationStep } = useContext(UserPropertiesContext)!;

  const methods = useForm<FormValues>({
    defaultValues: {
      skippedSteps: [],
    },
  });

  const { reset } = methods;

  const url = `${endpoints.RESERVATION}/${reservation?.Id}/registration-steps`;
  const [{ data: reservationRegistrationStepResult }, getReservationSteps] = useRequest<
    ReservationRegistrationStep[]
  >(url, 'get', {
    authToken: credentialsInfo?.token,
  });

  const updateURL = `${endpoints.RESERVATION}/${reservation?.Id}/registration-steps`;
  const [
    { data: updateReservationRegistration, loading: loadingReservationRegistration },
    updateRegistrationStep,
  ] = useRequest<ReservationRegistrationStep[]>(
    updateURL,
    'patch',
    {
      authToken: credentialsInfo?.token,
    },
    { manual: true },
  );

  const onSubmit = (data: FormValues) => {
    const payload = data.skippedSteps.map((step) => ({
      reservationRegistrationStepId: step.reservationRegistrationStepId,
      isSkippable: step.isSkippable,
    }));

    updateRegistrationStep({ data: payload });
  };

  useEffect(() => {
    if (!updateReservationRegistration) return;

    getReservationSteps();
    handleClose();
  }, [updateReservationRegistration]);

  useEffect(() => {
    if (!reservationRegistrationStepResult || !reservationRegistrationStepResult?.result)
      return;

    const merged = mergeRegistrationSteps(
      reservationRegistrationStepResult.result ?? [],
      propertyRegistrationStep ?? [],
    );
    setCombinedSteps(merged);

    const skippedDefaults = merged
      .filter((step) => step.isSkippable)
      .map((step) => ({
        reservationRegistrationStepId: step.reservationRegistrationStepId,
        propertyRegistrationStepId: step.propertyRegistrationStepId,
        isSkippable: true,
      }));

    reset({
      skippedSteps: skippedDefaults,
    });
  }, [reservationRegistrationStepResult, propertyRegistrationStep]);

  useEffect(() => {
    const incompleteSteps = steps.filter((step) => step.state === 'incomplete');

    if (incompleteSteps.length === 1) {
      setCurrentState('Unverified');
      return;
    }

    const allComplete = steps.every((step) => step.state === 'complete');

    if (allComplete) {
      setCurrentState('Verified');
    }
  }, [steps]);

  return (
    <FormProvider {...methods}>
      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Modal.Header closeButton className="border-0">
            <Modal.Title>Guest Portal Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <GuestPortalManagerCard reservation={reservation} />

            <Row className="align-items-center mb-4">
              <Col xs={2}>
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ width: 36, height: 36 }}
                >
                  <TicketPerforated size={25} />
                </div>
              </Col>
              <Col xs={4}>
                <div className="d-flex flex-column">
                  <span style={{ fontSize: '18px', fontWeight: '600' }}>
                    Registration
                  </span>
                </div>
              </Col>
              <Col xs={3}>
                <Badge className="status-badge" bg="secondary">
                  {capitalCase(currentState)}
                </Badge>
              </Col>

              <Col xs={3} style={{ justifyContent: 'end' }}>
                {renderMenuIcon()}
              </Col>
            </Row>

            {combinedSteps?.map((step) => (
              <ReservationStep key={step.tagId} step={step} />
            ))}
          </Modal.Body>
          <ModalFooter style={{ borderTop: 'none' }}>
            <Button className="cancel-button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="save-button"
              type="submit"
              disabled={loadingReservationRegistration}
            >
              {loadingReservationRegistration ? 'Loading' : 'Save'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </FormProvider>
  );
};
